import { Card } from "@/components/Card";
import { H3, P } from "@/components/Typography";
import { getContactSummary } from "@/utils/emailDashboardMetricsHelper";
import { faUserChart } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import {
  TooltipContentProps,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  BarChart,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

interface IProps {
  data: IGetEmailDashboardRS;
}

const ContactSummary = ({ data }: IProps) => {
  const { t } = useTranslation();
  const contactSummaryData = getContactSummary(data?.rawData);

  const basicFormatNumber = (number: number) =>
    new Intl.NumberFormat("en-US", {
      notation: "standard",
      compactDisplay: "short",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(number);

  const getChartTooltip = ({
    active,
    payload,
    label,
  }: TooltipContentProps<ValueType, NameType>) => {
    if (!active || !payload || !payload.length) return null;

    const verifiedContactsPayload = payload[0];
    const marketableContactsPayload = payload[1];

    return (
      <div className="flex flex-col rounded border border-gray-light bg-white p-10 shadow-sm">
        <span>{label}</span>
        <span
          style={{
            color: verifiedContactsPayload.color,
          }}>
          {t("email.dashboard.charts.contactSummary.tooltips.verifiedContacts")}
          : {basicFormatNumber(verifiedContactsPayload.value)}
        </span>
        <span
          style={{
            color: marketableContactsPayload.color,
          }}>
          {t(
            "email.dashboard.charts.contactSummary.tooltips.marketableContacts",
          )}
          : {basicFormatNumber(marketableContactsPayload.value)}
        </span>
      </div>
    );
  };

  return (
    <Card shadow additionalClassName="p-20 border border-gray-light">
      <div className="flex flex-row items-center gap-10">
        <FontAwesomeIcon icon={faUserChart} className="text-2xl text-primary" />
        <H3 className="font-bold">
          {t("email.dashboard.charts.contactSummary.title")}
        </H3>
      </div>
      <P className="mb-20">
        {t("email.dashboard.charts.contactSummary.description")}
      </P>
      <BarChart
        style={{
          width: "100%",
          maxWidth: "700px",
          maxHeight: "70vh",
          aspectRatio: 1.618,
        }}
        responsive
        data={contactSummaryData}>
        <XAxis
          dataKey="month"
          type="category"
          interval="equidistantPreserveStart"
          tick={{ fontSize: 10 }}
        />
        <YAxis
          width="auto"
          tickFormatter={value => basicFormatNumber(value)}
          tick={{ fontSize: 10 }}
        />
        <Tooltip content={t => getChartTooltip(t)} />
        <Bar
          type="monotone"
          dataKey="verifiedContacts"
          stroke="#10A4FF"
          fill="#10A4FF"
        />
        <Bar
          type="monotone"
          dataKey="marketableContacts"
          stroke="#62C3FF"
          fill="#62C3FF"
        />
      </BarChart>
    </Card>
  );
};

export default ContactSummary;
